import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './CourseContents.css';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL, { API_ENDPOINTS } from '../config/api';

const foldCourseText = (value) => String(value || '')
  .toLocaleLowerCase('tr-TR')
  .replace(/[çğıöşü]/g, (letter) => ({ ç: 'c', ğ: 'g', ı: 'i', ö: 'o', ş: 's', ü: 'u' }[letter]))
  .replace(/[^a-z0-9]+/g, '');

const isElectiveGroupCourse = (course) => {
  const type = foldCourseText(course?.course_type);
  const name = foldCourseText(course?.course_name);
  return (type.includes('secmeli') || type.includes('elective'))
    && (name.includes('secmeliders') || name.includes('electivecourse'));
};

const uniqueItemsById = (items) => {
  const seen = new Set();
  return items.filter((item) => {
    if (item.id === null || item.id === undefined) return false;
    const key = String(item.id);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const sortEducationTypes = (types) => {
  const rank = (name) => {
    const normalized = foldCourseText(name);
    if (normalized.includes('onlisans') || normalized.includes('associate')) return 0;
    if (normalized === 'lisans' || normalized.includes('bachelor')) return 1;
    if (normalized.includes('yukseklisans') || normalized.includes('master')) return 2;
    return 99;
  };

  return [...types].sort((a, b) =>
    rank(a.name) - rank(b.name) || String(a.name || '').localeCompare(String(b.name || ''), 'tr')
  );
};

// Sadece SPA oturumu boyunca filtreyi korur; tarayıcı yenilenince sıfırlanır.
let lastCourseContentsFilters = null;

const CourseContents = () => {
  const { t } = useTranslation();
  // Education Type States
  const [educationTypes, setEducationTypes] = useState([]);
  const [selectedEducationType, setSelectedEducationType] = useState(null);

  // Faculty States
  const [faculties, setFaculties] = useState([]);
  const [selectedFaculty, setSelectedFaculty] = useState(null);

  // Program States
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);

  // Course States
  const [courses, setCourses] = useState([]);
  const [coursesBySemester, setCoursesBySemester] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({}); // { "semester-courseId": true }
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseDetails, setCourseDetails] = useState({
    learningOutcomes: [],
    programOutcomes: [],
    weeklyTopics: {},
    courseResources: '',
    recommendedResources: ''
  });

  // Common States
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const [allowedEducationTypes, setAllowedEducationTypes] = useState([]);
  const [allowedFaculties, setAllowedFaculties] = useState([]);
  const [allowedPrograms, setAllowedPrograms] = useState([]);
  const [allowedCourses, setAllowedCourses] = useState([]);
  const [assignedCourseScopes, setAssignedCourseScopes] = useState([]);
  const [assignmentScopeLoaded, setAssignmentScopeLoaded] = useState(false);

  // Permission states
  const [coursePermissions, setCoursePermissions] = useState({});
  const [editingCourse, setEditingCourse] = useState(null);
  const [editingTopics, setEditingTopics] = useState([]);

  const navigate = useNavigate();

  // Akademisyen id'sini localStorage veya context'ten al
  const akademisyenId = sessionStorage.getItem('userRole') === 'akademisyen'
    ? sessionStorage.getItem('akademisyen_id') : null;

  // Yetki kontrolü fonksiyonu
  // eslint-disable-next-line no-unused-vars
  const checkCoursePermission = async (courseId) => {
    try {
      // Akademisyen bazlı izlence yetkisi kontrolü
      const response = await axios.get(`${API_BASE_URL}/api/admin/academic-syllabus-permission/${akademisyenId}`);
      return response.data.success && response.data.has_permission;
    } catch (error) {
      console.error('Yetki kontrolü hatası:', error);
      return false;
    }
  };

  // Ders yetkilerini kontrol et
  const checkAllCoursePermissions = async (courses) => {
    try {
      // Akademisyen bazlı izlence yetkisi kontrolü - tek seferde
      const response = await axios.get(`${API_BASE_URL}/api/admin/academic-syllabus-permission/${akademisyenId}`);
      
      if (response.data.success && response.data.has_permission) {
        // Akademisyenin izlence yetkisi varsa, tüm dersler için yetki ver
        const permissions = {};
        courses.forEach(course => {
          permissions[course.id] = true;
        });
        setCoursePermissions(permissions);
      } else {
        // Akademisyenin izlence yetkisi yoksa, hiçbir ders için yetki verme
        const permissions = {};
        courses.forEach(course => {
          permissions[course.id] = false;
        });
        setCoursePermissions(permissions);
      }
    } catch (error) {
      console.error('Yetki kontrolü hatası:', error);
      // Hata durumunda hiçbir ders için yetki verme
      const permissions = {};
      courses.forEach(course => {
        permissions[course.id] = false;
      });
      setCoursePermissions(permissions);
    }
  };

  // Düzenleme modunu başlat
  const startEditing = (course) => {
    setEditingCourse(course);
    // Mevcut konuları düzenleme formatına çevir
    const topics = Object.entries(courseDetails.weeklyTopics).map(([week, data]) => ({
      number: week,
      topic: data.topic,
      on_hazirlik: data.on_hazirlik || '',
      metodoloji: data.metodoloji || ''
    }));
    setEditingTopics(topics);
  };

  // Düzenleme modunu iptal et
  const cancelEditing = () => {
    setEditingCourse(null);
    setEditingTopics([]);
  };

  // Konu ekle
  const addTopic = () => {
    const newNumber = editingTopics.length + 1;
    setEditingTopics([...editingTopics, {
      number: newNumber.toString(),
      topic: '',
      on_hazirlik: '',
      metodoloji: ''
    }]);
  };

  // Konu sil
  const removeTopic = (index) => {
    const newTopics = editingTopics.filter((_, i) => i !== index);
    // Numaraları yeniden düzenle
    const renumberedTopics = newTopics.map((topic, i) => ({
      ...topic,
      number: (i + 1).toString()
    }));
    setEditingTopics(renumberedTopics);
  };

  // Konu güncelle
  const updateTopic = (index, field, value) => {
    const newTopics = [...editingTopics];
    newTopics[index] = { ...newTopics[index], [field]: value };
    setEditingTopics(newTopics);
  };

  // Değişiklikleri kaydet
  const saveChanges = async () => {
    try {
      setLoading(true);
      const response = await axios.put(API_ENDPOINTS.UPDATE_WEEKLY_TOPICS(editingCourse.id), {
        academic_id: akademisyenId,
        weekly_topics: editingTopics
      });

      if (response.data.success) {
        setSuccess('Haftalık konular başarıyla güncellendi');
        // Ders detaylarını yeniden yükle
        await fetchCourseDetails(editingCourse.id);
        cancelEditing();
      } else {
        setError(response.data.error || 'Güncelleme başarısız');
      }
    } catch (error) {
      setError('Güncelleme sırasında hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Hiyerarşiyi akademisyenin gerçek ders atamalarından üret. Düz izin
  // listeleri üst-alt ilişkisini kaybettiği için altında ders olmayan eğitim
  // türleri ekranda kalabiliyordu.
  useEffect(() => {
    if (akademisyenId) {
      setAssignmentScopeLoaded(false);
      axios.get(`${API_ENDPOINTS.AKADEMISYEN_COURSES_DETAILED}?akademisyen_id=${akademisyenId}`)
        .then(res => {
          const scopes = (Array.isArray(res.data) ? res.data : []).filter(scope =>
            scope.education_type_id != null &&
            scope.faculty_id != null &&
            scope.program_id != null &&
            scope.course_id != null
          );

          setAssignedCourseScopes(scopes);
          setAllowedEducationTypes([...new Set(scopes.map(scope => scope.education_type_id))]);
          setAllowedFaculties([...new Set(scopes.map(scope => scope.faculty_id))]);
          setAllowedPrograms([...new Set(scopes.map(scope => scope.program_id))]);
          setAllowedCourses([...new Set(scopes.map(scope => scope.course_id))]);
          const scopedEducationTypes = uniqueItemsById(scopes.map(scope => ({
            id: scope.education_type_id,
            name: scope.education_type_name
          })));
          setEducationTypes(sortEducationTypes(scopedEducationTypes));
        })
        .catch((error) => {
          console.error('Ders atama kapsamı yüklenemedi:', error);
          setAssignedCourseScopes([]);
          setAllowedEducationTypes([]);
          setAllowedFaculties([]);
          setAllowedPrograms([]);
          setAllowedCourses([]);
          setEducationTypes([]);
        })
        .finally(() => setAssignmentScopeLoaded(true));
    } else {
      setAssignmentScopeLoaded(true);
      fetchEducationTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [akademisyenId]);

  // Önceki sayfa ziyaretinden kalan seçim artık akademisyenin ders kapsamı
  // içinde değilse boş bir alt liste göstermeden seçimi temizle.
  useEffect(() => {
    if (!akademisyenId || !assignmentScopeLoaded) return;

    if (selectedEducationType && !assignedCourseScopes.some(scope =>
      String(scope.education_type_id) === String(selectedEducationType)
    )) {
      setSelectedEducationType(null);
      setSelectedFaculty(null);
      setSelectedProgram(null);
      setSelectedCourse(null);
      return;
    }

    if (selectedFaculty && !assignedCourseScopes.some(scope =>
      String(scope.education_type_id) === String(selectedEducationType) &&
      String(scope.faculty_id) === String(selectedFaculty)
    )) {
      setSelectedFaculty(null);
      setSelectedProgram(null);
      setSelectedCourse(null);
      return;
    }

    if (selectedProgram && !assignedCourseScopes.some(scope =>
      String(scope.faculty_id) === String(selectedFaculty) &&
      String(scope.program_id) === String(selectedProgram)
    )) {
      setSelectedProgram(null);
      setSelectedCourse(null);
    }
  }, [akademisyenId, assignmentScopeLoaded, assignedCourseScopes,
    selectedEducationType, selectedFaculty, selectedProgram]);

  // Sayfa geri açıldığında son filtreleri geri yükle (refresh'te korunmaz)
  useEffect(() => {
    if (lastCourseContentsFilters) {
      if (lastCourseContentsFilters.educationTypeId) setSelectedEducationType(lastCourseContentsFilters.educationTypeId);
      if (lastCourseContentsFilters.facultyId) setSelectedFaculty(lastCourseContentsFilters.facultyId);
      if (lastCourseContentsFilters.programId) setSelectedProgram(lastCourseContentsFilters.programId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtre değiştikçe bellekte sakla (detay sayfasından geri dönüşte korunsun)
  useEffect(() => {
    lastCourseContentsFilters = {
      educationTypeId: selectedEducationType || null,
      facultyId: selectedFaculty || null,
      programId: selectedProgram || null
    };
  }, [selectedEducationType, selectedFaculty, selectedProgram]);

  // Yalnız seçilen eğitim türünde atanmış dersi bulunan fakülteleri göster.
  useEffect(() => {
    if (!selectedEducationType) {
      setFaculties([]);
      return;
    }
    if (akademisyenId) {
      if (!assignmentScopeLoaded) return;
      const scopedFaculties = uniqueItemsById(
        assignedCourseScopes
          .filter(scope => String(scope.education_type_id) === String(selectedEducationType))
          .map(scope => ({
            id: scope.faculty_id,
            name: scope.faculty_name,
            educationTypeId: scope.education_type_id
          }))
      );
      setFaculties(scopedFaculties);
    } else {
      fetchFaculties(selectedEducationType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEducationType, assignedCourseScopes, assignmentScopeLoaded, akademisyenId]);

  // Yalnız seçilen fakültede atanmış dersi bulunan programları göster.
  useEffect(() => {
    if (!selectedFaculty) {
      setPrograms([]);
      return;
    }
    if (akademisyenId) {
      if (!assignmentScopeLoaded) return;
      const scopedPrograms = uniqueItemsById(
        assignedCourseScopes
          .filter(scope =>
            String(scope.education_type_id) === String(selectedEducationType) &&
            String(scope.faculty_id) === String(selectedFaculty)
          )
          .map(scope => ({
            id: scope.program_id,
            name: scope.program_name,
            facultyId: scope.faculty_id,
            educationTypeId: scope.education_type_id
          }))
      );
      setPrograms(scopedPrograms);
    } else {
      fetchPrograms(selectedFaculty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFaculty, selectedEducationType, assignedCourseScopes, assignmentScopeLoaded, akademisyenId]);

  // allowedCourses güncellendiğinde ve program seçildiğinde dersleri çek
  useEffect(() => {
    if (!selectedProgram || (akademisyenId && allowedCourses.length === 0)) {
      setCourses([]);
      setCoursesBySemester({});
      return;
    }
    fetchCourses(selectedProgram);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProgram, allowedCourses]);

  // Add useEffect to fetch course details when course changes
  useEffect(() => {
    if (selectedCourse) {
      fetchCourseDetails(selectedCourse);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourse]);

  // Education Type Functions
  const fetchEducationTypes = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.EDUCATION_TYPES);
      let types = response.data;
      if (akademisyenId) {
        types = types.filter(type => allowedEducationTypes.map(String).includes(String(type.id)));
      }
      setEducationTypes(sortEducationTypes(types));
      setError('');
    } catch (err) {
      setError('Eğitim türleri yüklenirken bir hata oluştu.');
      console.error('Error fetching education types:', err);
    }
  };

  // Faculty Functions
  const fetchFaculties = async (educationTypeId = null) => {
    try {
      setLoading(true);
      if (!educationTypeId) {
        setFaculties([]);
        return;
      }
      const response = await axios.get(`${API_BASE_URL}/api/faculties?educationTypeId=${educationTypeId}`);
      let faculties = response.data;
      if (akademisyenId) {
        faculties = faculties.filter(faculty => allowedFaculties.map(String).includes(String(faculty.id)));
      }
      setFaculties(faculties);
      setError('');
    } catch (err) {
      console.error('Fakülteler yüklenirken hata:', err);
      setError(err.response?.data?.error || 'Fakülteler yüklenirken bir hata oluştu.');
      setFaculties([]);
    } finally {
      setLoading(false);
    }
  };

  // Program Functions
  const fetchPrograms = async (facultyId = null) => {
    try {
      setLoading(true);
      if (!facultyId) {
        setPrograms([]);
        return;
      }
      const response = await axios.get(`${API_BASE_URL}/api/programs?facultyId=${facultyId}`);
      let programs = response.data;
      if (akademisyenId) {
        programs = programs.filter(program => allowedPrograms.map(String).includes(String(program.id)));
      }
      setPrograms(programs);
      setError('');
    } catch (err) {
      console.error('Programlar yüklenirken hata:', err);
      setError(err.response?.data?.error || 'Programlar yüklenirken bir hata oluştu.');
      setPrograms([]);
    } finally {
      setLoading(false);
    }
  };

  // Course Functions
  const fetchCourses = async (programId = null) => {
    try {
      if (!programId) {
        setCourses([]);
        setCoursesBySemester({});
        return;
      }
      const response = await axios.get(`${API_BASE_URL}/api/courses?programId=${programId}`);
      let courses = response.data;
      if (akademisyenId || allowedCourses.length > 0) {
        courses = courses.filter(course => allowedCourses.map(String).includes(String(course.id)));
      }
      setCourses(courses);
      // Dersler yüklendikten sonra yetki kontrolü yap
      if (courses.length > 0) {
        checkAllCoursePermissions(courses);
      }
      
      // Dönem bazlı dersleri de getir
      fetchCoursesBySemester(programId);
    } catch (err) {
      setError('Dersler yüklenirken bir hata oluştu.');
      setCourses([]);
      setCoursesBySemester({});
    }
  };

  const fetchCoursesBySemester = async (programId) => {
    try {
      if (!programId) {
        setCoursesBySemester({});
        return;
      }
      // Akademisyen ID'si ile çağrı yap
      const url = akademisyenId 
        ? `${API_BASE_URL}/api/programs/${programId}/courses-by-semester?akademisyen_id=${akademisyenId}`
        : `${API_BASE_URL}/api/programs/${programId}/courses-by-semester`;
      const response = await axios.get(url);
      setCoursesBySemester(response.data || {});
    } catch (err) {
      console.error('Dönem bazlı dersler yüklenirken hata:', err);
      // Fallback: Normal courses endpoint'inden dersleri al ve dönem bazlı grupla
      try {
        // Akademisyen ID'si ile çağrı yap
        const coursesUrl = akademisyenId 
          ? `${API_BASE_URL}/api/akademisyen/courses?akademisyen_id=${akademisyenId}&programId=${programId}`
          : `${API_BASE_URL}/api/courses?programId=${programId}`;
        const coursesResponse = await axios.get(coursesUrl);
        let courses = coursesResponse.data;
        
        // Eğer akademisyen ID varsa ve programId filtresi yoksa, programId'ye göre filtrele
        if (akademisyenId && programId) {
          courses = courses.filter(c => c.programId === parseInt(programId));
        }
        
        // Dönem bazlı gruplama yap
        const grouped = {};
        const regularCourses = [];
        const groups = [];
        
        // Önce dersleri kategorize et
        courses.forEach(course => {
          const semester = String(course.donem || course.semester || '0');
          if (!grouped[semester]) {
            grouped[semester] = [];
          }
          
          const isGroup = isElectiveGroupCourse(course);
          
          if (isGroup) {
            groups.push({...course, semester});
          } else {
            regularCourses.push({...course, semester});
          }
        });
        
        // Normal dersleri ekle
        regularCourses.forEach(course => {
          grouped[course.semester].push({
            id: course.id,
            course_code: course.course_code,
            course_name: course.course_name,
            course_type: course.course_type,
            teorik: course.teorik || 0,
            uygulama: course.uygulama || 0,
            laboratuvar: course.laboratuvar || 0,
            kredi: parseFloat(course.kredi) || 0,
            akts: parseFloat(course.akts) || 0,
            is_group: false
          });
        });
        
        // Seçmeli ders gruplarını ekle (alt dersleri ile birlikte)
        groups.forEach(group => {
          const courseData = {
            id: group.id,
            course_code: group.course_code,
            course_name: group.course_name,
            course_type: group.course_type,
            teorik: group.teorik || 0,
            uygulama: group.uygulama || 0,
            laboratuvar: group.laboratuvar || 0,
            kredi: parseFloat(group.kredi) || 0,
            akts: parseFloat(group.akts) || 0,
            is_group: true,
            children: []
          };
          
          grouped[group.semester].push(courseData);
        });
        
        setCoursesBySemester(grouped);
      } catch (fallbackErr) {
        console.error('Fallback de başarısız:', fallbackErr);
        setCoursesBySemester({});
      }
    }
  };

  const toggleGroup = (semester, courseId) => {
    const key = `${semester}-${courseId}`;
    setExpandedGroups(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Course Details Functions
  const fetchCourseDetails = async (courseId) => {
    try {
      setLoading(true);
      setError(null);

      // Infer course code for course-info fetch
      const selected = (courses || []).find(c => c.id === courseId) || {};
      const courseCode = selected.code || selected.course_code;

      const [learningOutcomesRes, programOutcomesRes, weeklyTopicsRes, courseInfoRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/course-outcomes/${courseId}`),
        axios.get(`${API_BASE_URL}/api/program-outcomes/${selectedProgram}`),
        axios.get(`${API_BASE_URL}/api/weekly-topics/${courseId}`),
        courseCode ? axios.get(`${API_BASE_URL}/api/programs/${selectedProgram}/courses/${courseCode}/course-info`) : Promise.resolve({ data: null })
      ]);

      const sortedLearningOutcomes = (learningOutcomesRes.data.learning || []).sort((a, b) => {
        const aNum = parseInt(a.code.replace('ÖÇ', ''));
        const bNum = parseInt(b.code.replace('ÖÇ', ''));
        return aNum - bNum;
      });

      const sortedProgramOutcomes = (programOutcomesRes.data.outcomes || []).sort((a, b) => {
        const aNum = parseInt(a.code.replace('PÇ', ''));
        const bNum = parseInt(b.code.replace('PÇ', ''));
        return aNum - bNum;
      });

      const sortedWeeklyTopics = {};
      if (weeklyTopicsRes.data) {
        const sortedWeeks = Object.entries(weeklyTopicsRes.data)
          .sort((a, b) => parseInt(a[0]) - parseInt(b[0]));
        sortedWeeks.forEach(([week, data]) => {
          sortedWeeklyTopics[week] = data;
        });
      }

      const ci = courseInfoRes?.data || {};
      setCourseDetails({
        learningOutcomes: sortedLearningOutcomes,
        programOutcomes: sortedProgramOutcomes,
        weeklyTopics: sortedWeeklyTopics,
        courseResources: ci.ders_kaynaklari || ci.course_resources || '',
        recommendedResources: ci.onerilen_kaynaklar || ci.recommended_resources || ''
      });
    } catch (err) {
      console.error('Ders detayları yüklenirken hata:', err);
      setError('Ders detayları yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Click Handlers
  const handleEducationTypeClick = (educationType) => {
    setSelectedEducationType(selectedEducationType === educationType.id ? null : educationType.id);
    setSelectedFaculty(null);
    setSelectedProgram(null);
    setSelectedCourse(null);
  };

  const handleFacultyClick = (faculty) => {
    setSelectedFaculty(selectedFaculty === faculty.id ? null : faculty.id);
    setSelectedProgram(null);
    setSelectedCourse(null);
  };

  const handleProgramClick = (program) => {
    setSelectedProgram(selectedProgram === program.id ? null : program.id);
    setSelectedCourse(null);
  };

  // eslint-disable-next-line no-unused-vars
  const handleCourseClick = (course) => {
    const programId = selectedProgram; // ID already
    const courseCode = course.code || course.course_code;
    navigate(`/course/${programId}/${courseCode}`);
  };

  const handleEditCourse = (course) => {
    alert(`${course.name} dersi düzenlenecek!`);
    // İleride burada modal açabilir veya başka bir sayfaya yönlendirebilirsin
  };

  // Outcome mapping kontrolü
  // eslint-disable-next-line no-unused-vars
  const checkCourseMapping = async (programId, courseId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/check-outcome-mappings/${programId}/${courseId}`);
      return response.data;
    } catch (error) {
      console.error('Mapping kontrolü sırasında hata:', error);
      return null;
    }
  };

  const handleGoToQuestions = async (course) => {
    setSelectedCourse(course.id);
    const programId = selectedProgram;
    const courseCode = course.code || course.course_code;
    navigate(`/course/${programId}/${courseCode}`);
  };

  return (
    <div className="course-contents-container">
      

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Data Display Sections */}
      <div className="data-sections">
        <div className="data-section">
          <h2>{t('akademisyen.educationTypes')}</h2>
          <div className="data-list">
            {educationTypes.map(type => (
              <div 
                key={type.id} 
                className={`data-item ${selectedEducationType === type.id ? 'selected' : ''}`}
                onClick={() => handleEducationTypeClick(type)}
                style={{ cursor: 'pointer' }}
              >
                <span>{type.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="data-section">
          <h2>{t('akademisyen.faculties')}</h2>
          <div className="data-list">
            {!selectedEducationType ? (
              <div className="no-data">Lütfen bir eğitim türü seçin</div>
            ) : faculties.length === 0 ? (
              <div className="no-data">Bu eğitim türüne ait fakülte bulunamadı</div>
            ) : (
              faculties.map(faculty => (
                <div 
                  key={faculty.id} 
                  className={`data-item ${selectedFaculty === faculty.id ? 'selected' : ''}`}
                  onClick={() => handleFacultyClick(faculty)}
                  style={{ cursor: 'pointer' }}
                >
                  <span>{faculty.name}</span>
                  <span className="sub-text">
                    {educationTypes.find(type => type.id === faculty.educationTypeId)?.name}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="data-section">
          <h2>{t('akademisyen.programs')}</h2>
          <div className="data-list">
            {!selectedFaculty ? (
              <div className="no-data">Lütfen bir fakülte seçin</div>
            ) : programs.length === 0 ? (
              <div className="no-data">Bu fakülteye ait program bulunamadı</div>
            ) : (
              programs.map(program => (
                <div 
                  key={program.id} 
                  className={`data-item ${selectedProgram === program.id ? 'selected' : ''}`}
                  onClick={() => handleProgramClick(program)}
                  style={{ cursor: 'pointer' }}
                >
                  <span>{program.name}</span>
                  <span className="sub-text">
                    {faculties.find(f => f.id === program.facultyId)?.name} - 
                    {educationTypes.find(type => type.id === program.educationTypeId)?.name}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="data-section">
          <h2>{t('akademisyen.courses')}</h2>
          <div className="data-list">
            {!selectedProgram ? (
              <div className="no-data">Lütfen bir program seçin</div>
            ) : Object.keys(coursesBySemester).length === 0 ? (
              <div className="no-data">Bu programa ait ders bulunamadı</div>
            ) : (
              // Tüm dönemlerdeki dersleri tek listede göster
              Object.keys(coursesBySemester).sort((a, b) => parseInt(a) - parseInt(b)).flatMap(semester => {
                const semesterCourses = coursesBySemester[semester];
                if (!Array.isArray(semesterCourses)) {
                  return [];
                }
                return semesterCourses.map(course => {
                  const isGroup = course.is_group;
                  const isExpanded = expandedGroups[`${semester}-${course.id}`];
                  const courseObj = courses.find(c => c.id === course.id) || {
                    id: course.id,
                    code: course.course_code || course.code,
                    name: course.course_name || course.name,
                    programName: '',
                    semester: semester
                  };
                  
                  return (
                    <React.Fragment key={`${semester}-${course.id}`}>
                      <div className="course-wrapper">
                        <div 
                          className={`data-item course-item ${selectedCourse === course.id ? 'selected' : ''} ${isGroup ? 'course-group' : ''}`}
                          onClick={() => {
                            if (!isGroup) {
                              const courseForNav = {
                                id: course.id,
                                code: course.course_code || course.code,
                                course_code: course.course_code || course.code,
                                name: course.course_name || course.name,
                                course_name: course.course_name || course.name
                              };
                              handleGoToQuestions(courseForNav);
                            }
                          }}
                          style={{ cursor: isGroup ? 'default' : 'pointer' }}
                        >
                          <div className="course-header">
                            <span>{course.course_name || course.name || 'Ders Adı Yok'} ({course.course_code || course.code || 'Kod Yok'})</span>
                            <span className="sub-text">
                              {courseObj.programName || ''} - {semester} Dönemi
                            </span>
                          </div>
                        </div>
                        
                        {/* Seçmeli ders grubu için küçük expand/collapse alanı */}
                        {isGroup && (
                          <div className="elective-group-expand">
                            <button 
                              className="elective-expand-btn"
                              onClick={() => {
                                console.log('DEBUG: Grup tıklandı:', course.course_name, 'children:', course.children, 'length:', course.children?.length);
                                toggleGroup(semester, course.id);
                              }}
                            >
                              {isExpanded ? '▼ Alt Dersleri Gizle' : '▶ Alt Dersleri Göster'}
                            </button>
                            {isExpanded && course.children && Array.isArray(course.children) && course.children.length > 0 && (
                              <div className="elective-children-list">
                                {course.children.map(child => {
                                  const childObj = courses.find(c => c.id === child.id) || {
                                    id: child.id,
                                    code: child.course_code,
                                    name: child.course_name,
                                    programName: '',
                                    semester: semester
                                  };
                                  
                                  return (
                                    <div key={child.id} className="course-wrapper course-child-wrapper">
                                      <div 
                                        className={`data-item course-item ${selectedCourse === child.id ? 'selected' : ''}`}
                                        onClick={() => {
                                          // Normal dersler gibi detay sayfasına yönlendir
                                          const childCourseObj = {
                                            id: child.id,
                                            code: child.course_code || child.code,
                                            name: child.course_name || child.name,
                                            course_code: child.course_code || child.code,
                                            course_name: child.course_name || child.name
                                          };
                                          handleGoToQuestions(childCourseObj);
                                        }}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        <div className="course-header">
                                          <span>{child.course_name || child.name || 'Ders Adı Yok'} ({child.course_code || child.code || 'Kod Yok'})</span>
                                          <span className="sub-text">
                                            {childObj.programName || ''} - {semester} Dönemi
                                          </span>
                                        </div>
                                      </div>
                                      
                                      {selectedCourse === child.id && (
                                        <div className="course-details">
                                          <button className="edit-btn" onClick={() => handleEditCourse(childObj)}>
                                            Düzenle
                                          </button>
                                          <div className="details-section">
                                            <h3>Ders Kaynakları</h3>
                                            {courseDetails.courseResources ? (
                                              <div style={{ whiteSpace: 'pre-line' }}>{courseDetails.courseResources}</div>
                                            ) : (
                                              <p className="no-data">-</p>
                                            )}
                                            {courseDetails.recommendedResources ? (
                                              <>
                                                <h4 style={{marginTop: '12px'}}>Önerilen Kaynaklar</h4>
                                                <div style={{ whiteSpace: 'pre-line' }}>{courseDetails.recommendedResources}</div>
                                              </>
                                            ) : null}
                                          </div>
                                          <div className="details-section">
                                            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                                            <h3>Dersin öğrenme çıktıları</h3>
                                              <button className="edit-btn" onClick={() => alert('Dersin öğrenme çıktıları düzenlenecek!')}>Düzenle</button>
                                            </div>
                                            {courseDetails.learningOutcomes.length > 0 ? (
                                              <ul className="outcomes-list">
                                                {courseDetails.learningOutcomes.map((outcome, index) => (
                                                  <li key={outcome.id}>
                                                    <strong>{index + 1}.</strong> {outcome.description}
                                                  </li>
                                                ))}
                                              </ul>
                                            ) : (
                                              <p className="no-data">Bu ders için henüz ders Dersin Öğrenme Çıktısı eklenmemiş.</p>
                                            )}
                                          </div>

                                          <div className="details-section">
                                            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                                            <h3>Program Çıktıları</h3>
                                              <button className="edit-btn" onClick={() => alert('Program Çıktıları düzenlenecek!')}>Düzenle</button>
                                            </div>
                                            {courseDetails.programOutcomes.length > 0 ? (
                                              <ul className="outcomes-list">
                                                {courseDetails.programOutcomes.map((outcome, index) => (
                                                  <li key={outcome.id}>
                                                    <strong>{index + 1}.</strong> {outcome.description}
                                                  </li>
                                                ))}
                                              </ul>
                                            ) : (
                                              <p className="no-data">Bu ders için henüz program çıktısı eklenmemiş.</p>
                                            )}
                                          </div>

                                          <div className="details-section">
                                            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                                            <h3>Haftalık Konular</h3>
                                              {coursePermissions[child.id] && (
                                                <button 
                                                  className="edit-btn" 
                                                  onClick={() => startEditing(childObj)}
                                                >
                                                  Düzenle
                                                </button>
                                              )}
                                            </div>
                                            {Object.keys(courseDetails.weeklyTopics).length > 0 ? (
                                              <ul className="topics-list">
                                                {Object.entries(courseDetails.weeklyTopics).map(([week, data]) => (
                                                  <li key={week}>
                                                    <strong>{week}.</strong> {data.topic}
                                                  </li>
                                                ))}
                                              </ul>
                                            ) : (
                                              <p className="no-data">Bu ders için henüz haftalık konu eklenmemiş.</p>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {selectedCourse === course.id && !isGroup && (
                    <div className="course-details">
                            <button className="edit-btn" onClick={() => handleEditCourse(courseObj)}>
                        Düzenle
                      </button>
                      <div className="details-section">
                        <h3>Ders Kaynakları</h3>
                        {courseDetails.courseResources ? (
                          <div style={{ whiteSpace: 'pre-line' }}>{courseDetails.courseResources}</div>
                        ) : (
                          <p className="no-data">-</p>
                        )}
                        {courseDetails.recommendedResources ? (
                          <>
                            <h4 style={{marginTop: '12px'}}>Önerilen Kaynaklar</h4>
                            <div style={{ whiteSpace: 'pre-line' }}>{courseDetails.recommendedResources}</div>
                          </>
                        ) : null}
                      </div>
                      <div className="details-section">
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                        <h3>Dersin öğrenme çıktıları</h3>
                          <button className="edit-btn" onClick={() => alert('Dersin öğrenme çıktıları düzenlenecek!')}>Düzenle</button>
                        </div>
                        {courseDetails.learningOutcomes.length > 0 ? (
                          <ul className="outcomes-list">
                            {courseDetails.learningOutcomes.map((outcome, index) => (
                              <li key={outcome.id}>
                                <strong>{index + 1}.</strong> {outcome.description}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="no-data">Bu ders için henüz ders Dersin Öğrenme Çıktısı eklenmemiş.</p>
                        )}
                      </div>

                      <div className="details-section">
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                        <h3>Program Çıktıları</h3>
                          <button className="edit-btn" onClick={() => alert('Program Çıktıları düzenlenecek!')}>Düzenle</button>
                        </div>
                        {courseDetails.programOutcomes.length > 0 ? (
                          <ul className="outcomes-list">
                            {courseDetails.programOutcomes.map((outcome, index) => (
                              <li key={outcome.id}>
                                <strong>{index + 1}.</strong> {outcome.description}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="no-data">Bu ders için henüz program çıktısı eklenmemiş.</p>
                        )}
                      </div>

                      <div className="details-section">
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                        <h3>Haftalık Konular</h3>
                          {coursePermissions[course.id] && (
                            <button 
                              className="edit-btn" 
                                    onClick={() => startEditing(courseObj)}
                            >
                              Düzenle
                            </button>
                          )}
                        </div>
                        {Object.keys(courseDetails.weeklyTopics).length > 0 ? (
                          <ul className="topics-list">
                            {Object.entries(courseDetails.weeklyTopics).map(([week, data]) => (
                              <li key={week}>
                                <strong>{week}.</strong> {data.topic}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="no-data">Bu ders için henüz haftalık konu eklenmemiş.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                    </React.Fragment>
                  );
                });
              })
            )}
          </div>
        </div>
      </div>

      {/* Düzenleme Modalı */}
      {editingCourse && (
        <div className="modal-overlay" onClick={cancelEditing}>
          <div className="modal-content edit-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingCourse.course_name} - Haftalık Konular Düzenle</h3>
              <button className="modal-close-btn" onClick={cancelEditing}>&times;</button>
            </div>
            
            <div className="modal-body">
              <div className="topics-editor">
                {editingTopics.map((topic, index) => (
                  <div key={index} className="topic-editor-item">
                    <div className="topic-number">
                      <label>Hafta:</label>
                      <input
                        type="text"
                        value={topic.number}
                        onChange={(e) => updateTopic(index, 'number', e.target.value)}
                        className="topic-input"
                      />
                    </div>
                    <div className="topic-content">
                      <label>Konu:</label>
                      <input
                        type="text"
                        value={topic.topic}
                        onChange={(e) => updateTopic(index, 'topic', e.target.value)}
                        className="topic-input"
                        placeholder="Konu başlığı"
                      />
                    </div>
                    <div className="topic-methodology">
                      <label>Metodoloji:</label>
                      <input
                        type="text"
                        value={topic.metodoloji}
                        onChange={(e) => updateTopic(index, 'metodoloji', e.target.value)}
                        className="topic-input"
                        placeholder="Metodoloji"
                      />
                    </div>
                    <div className="topic-preparation">
                      <label>Ön Hazırlık:</label>
                      <input
                        type="text"
                        value={topic.on_hazirlik}
                        onChange={(e) => updateTopic(index, 'on_hazirlik', e.target.value)}
                        className="topic-input"
                        placeholder="Ön hazırlık"
                      />
                    </div>
                    <button 
                      className="remove-topic-btn"
                      onClick={() => removeTopic(index)}
                      disabled={editingTopics.length === 1}
                    >
                      Sil
                    </button>
                  </div>
                ))}
                
                <button className="add-topic-btn" onClick={addTopic}>
                  + Konu Ekle
                </button>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn-cancel" onClick={cancelEditing}>
                İptal
              </button>
              <button 
                className="btn-save" 
                onClick={saveChanges}
                disabled={loading}
              >
                {loading ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseContents; 
